<template>
  <!-- display: contents root: the sticky header and the fixed mobile drawer
       must not share a stacking context, or the overlay covers the drawer -->
  <div class="esign-navroot">
    <header class="esign-navbar">
      <div class="esign-navbar__inner">
        <v-btn
          icon
          dark
          class="esign-navbar__burger"
          aria-label="Buka menu navigasi"
          @click.stop="inputValue = !inputValue"
        >
          <v-icon>mdi-menu</v-icon>
        </v-btn>

        <div class="esign-logo" role="img" aria-label="e-Sign Universitas Indonesia">
          <span class="esign-logo__emblem"><img src="../../assets/logo-ui.png" alt=""></span>
          <span class="esign-logo__divider" aria-hidden="true"></span>
          <span class="esign-logo__text" aria-hidden="true">
            <span class="esign-logo__app">E-Sign</span>
            <span class="esign-logo__uni">Universitas</span>
            <span class="esign-logo__uni">Indonesia</span>
          </span>
        </div>

        <!-- Desktop: horizontal menu -->
        <nav class="esign-navbar__menu" aria-label="Menu utama">
          <v-menu
            v-for="group in navGroups"
            :key="'group-' + group.id"
            offset-y
            bottom
            attach=".esign-navbar"
            content-class="esign-navmenu"
          >
            <template v-slot:activator="{ on, attrs }">
              <button
                type="button"
                class="esign-navlink"
                :class="{ 'esign-navlink--active': isGroupActive(group) }"
                v-bind="attrs"
                v-on="on"
              >
                <v-icon small>{{ group.icon }}</v-icon>
                <span>{{ titleCase(group.nama) }}</span>
                <v-icon small class="esign-navlink__chevron">mdi-chevron-down</v-icon>
              </button>
            </template>
            <v-list dense>
              <v-list-item
                v-for="child in childrenOf(group)"
                :key="'child-' + child.id"
                :to="child.route"
              >
                <v-list-item-icon>
                  <v-icon small>{{ child.icon }}</v-icon>
                </v-list-item-icon>
                <v-list-item-title>{{ titleCase(child.nama) }}</v-list-item-title>
              </v-list-item>
            </v-list>
          </v-menu>

          <router-link
            v-for="item in navItems"
            :key="'item-' + item.id"
            :to="item.route"
            class="esign-navlink"
            active-class="esign-navlink--active"
          >
            <span class="esign-navlink__icon" @click="onBadgeClick(item)">
              <v-badge
                :value="badgeCount(item.nama)"
                :content="badgeCount(item.nama)"
                color="error"
                overlap
              >
                <v-icon small>{{ item.icon }}</v-icon>
              </v-badge>
            </span>
            <span>{{ titleCase(item.nama) }}</span>
          </router-link>
        </nav>

        <v-spacer></v-spacer>

        <div class="esign-navbar__actions">
          <!-- Extra actions (notifications) from the parent layout -->
          <slot />

          <v-menu
            offset-y
            bottom
            left
            :close-on-content-click="false"
            attach=".esign-navbar"
            content-class="esign-usermenu"
          >
            <template v-slot:activator="{ on, attrs }">
              <button type="button" class="esign-user" aria-label="Menu akun" v-bind="attrs" v-on="on">
                <span class="esign-user__avatar" aria-hidden="true">{{ userInitials }}</span>
                <span class="esign-user__meta">
                  <span class="esign-user__name">{{ displayName }}</span>
                  <span v-if="roleName" class="esign-user__role">{{ roleName }}</span>
                </span>
                <v-icon small class="esign-user__chevron">mdi-chevron-down</v-icon>
              </button>
            </template>
            <v-card class="esign-usercard" width="300" max-width="calc(100vw - 24px)">
              <div class="esign-usercard__head">
                <span class="esign-usercard__avatar" aria-hidden="true">{{ userInitials }}</span>
                <div class="esign-usercard__meta">
                  <p class="esign-usercard__name">{{ displayName }}</p>
                  <p class="esign-usercard__sub">{{ currentUser.username }}</p>
                  <p v-if="currentUser.pengguna && currentUser.pengguna.jabatan" class="esign-usercard__sub">{{ currentUser.pengguna.jabatan }}</p>
                </div>
              </div>
              <div class="esign-usercard__body">
                <label class="esign-field-label" for="selectPeran">Peran aktif</label>
                <v-select
                  id ="selectPeran"
                  hide-details
                  prepend-inner-icon="mdi-account-switch-outline"
                  :items="perans"
                  :item-text="item => item.nama"
                  :item-value="item => item.kode_peran"
                  placeholder="Pilih Peran"
                  required
                  dense
                  outlined
                  v-model="peranselected"
                  @input="$v.peranselected.$touch()"
                  @blur="$v.peranselected.$touch()"
                  @keydown.enter.prevent
                  @change="toProfil()"
                  >
                </v-select>
              </div>
              <v-divider></v-divider>
              <div class="esign-usercard__foot">
                <v-btn block text color="error" @click="$emit('logout')">
                  <v-icon left>mdi-logout-variant</v-icon> Logout
                </v-btn>
              </div>
            </v-card>
          </v-menu>
        </div>
      </div>
    </header>

    <!-- Tablet / mobile: slide-in menu -->
    <v-navigation-drawer
      id="app-drawer"
      v-model="inputValue"
      fixed
      temporary
      color="drawer"
      :width="288"
    >
      <div class="esign-drawer-brand">
        <div class="esign-logo esign-logo--compact" role="img" aria-label="e-Sign Universitas Indonesia">
          <span class="esign-logo__emblem"><img src="../../assets/logo-ui.png" alt=""></span>
          <span class="esign-logo__divider" aria-hidden="true"></span>
          <span class="esign-logo__text" aria-hidden="true">
            <span class="esign-logo__app">E-Sign</span>
            <span class="esign-logo__uni">Universitas</span>
            <span class="esign-logo__uni">Indonesia</span>
          </span>
        </div>
        <v-spacer></v-spacer>
        <v-btn icon dark aria-label="Tutup menu" @click="inputValue = false">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </div>

      <div class="esign-profile">
        <span class="esign-profile__avatar" aria-hidden="true">{{ userInitials }}</span>
        <div class="esign-profile__meta">
          <p class="esign-profile__name">{{ currentUser.username }}</p>
          <p v-if="currentUser.pengguna && currentUser.pengguna.jabatan" class="esign-profile__role">{{ currentUser.pengguna.jabatan }}</p>
        </div>
      </div>

      <div class="esign-role">
        <label class="esign-section-label" for="selectPeranMobile">Peran aktif</label>
        <v-select
          id="selectPeranMobile"
          class="esign-role__select"
          hide-details
          prepend-inner-icon="mdi-account-switch-outline"
          background-color="white"
          :items="perans"
          :item-text="item => item.nama"
          :item-value="item => item.kode_peran"
          placeholder="Pilih Peran"
          required
          dense
          outlined
          v-model="peranselected"
          @input="$v.peranselected.$touch()"
          @blur="$v.peranselected.$touch()"
          @keydown.enter.prevent
          @change="toProfil()"
        >
        </v-select>
      </div>

      <p class="esign-section-label esign-section-label--menu">Menu</p>

      <v-list class="esign-nav">
        <v-list-item class="menu">
          <v-list class="menu-drawer">
            <v-list density="compact" nav>
              <v-list-group
                v-for="group in navGroups"
                :key="'m-group-' + group.id"
                :value="isGroupActive(group)"
                :prepend-icon="group.icon"
              >
                <template v-slot:activator>
                  <v-list-item-title class="white--text">{{ titleCase(group.nama) }}</v-list-item-title>
                </template>
                <v-list-item
                  v-for="child in childrenOf(group)"
                  :key="'m-child-' + child.id"
                  style="padding-left: 40px;"
                  :to="child.route"
                >
                  <v-list-item-icon>
                    <v-icon class="white--text">{{ child.icon }}</v-icon>
                  </v-list-item-icon>
                  <v-list-item-title>{{ titleCase(child.nama) }}</v-list-item-title>
                </v-list-item>
              </v-list-group>
              <v-list-item
                v-for="item in navItems"
                :key="'m-item-' + item.id"
                :to="item.route"
              >
                <v-list-item-icon>
                  <span @click="onBadgeClick(item)">
                    <v-badge
                      :value="badgeCount(item.nama)"
                      :content="badgeCount(item.nama)"
                      color="error"
                      overlap
                    >
                      <v-icon class="white--text">{{ item.icon }}</v-icon>
                    </v-badge>
                  </span>
                </v-list-item-icon>
                <v-list-item-content>
                  <v-list-item-title class="white--text">{{ titleCase(item.nama) }}</v-list-item-title>
                </v-list-item-content>
              </v-list-item>
            </v-list>
          </v-list>
        </v-list-item>
      </v-list>

      <div class="esign-drawer-foot">
        <v-btn block outlined dark @click="$emit('logout')">
          <v-icon left>mdi-logout-variant</v-icon> Logout
        </v-btn>
      </div>
    </v-navigation-drawer>

    <v-snackbar
      v-model="snackbar"
      :color="snackbarColor"
      :timeout="8000"
      :top="true"
    >
      {{ snackbarText }}
      <v-btn dark text @click="snackbar = false">Close</v-btn>
    </v-snackbar>
  </div>
</template>
<script>
// Utilities
import { mapMutations, mapState } from "vuex";
import { apiServer } from "../../setup-axios";

export default {
  props: {
    opened: {
      type: Boolean,
      default: false,
    },
  },
  data: () => ({
    drawer: true,
    menuItems: [
      { id: 1, title: 'Profil', icon: 'mdi-account', route:'/profil', tooltip: 'Profil' },
      { id: 2, title: 'Organisasi', icon: 'mdi-office-building', route:'/organisasi', tooltip: ' Organisasi (PAU/Fakultas/Sekolah)' },
      { id: 3, title: 'Jenis Dokumen', icon: 'mdi-file-document', route:'/jenis-dokumen', tooltip: 'Jenis Dokumen' },
      { id: 4, title: 'Jenis Dokumen Organisasi', icon: 'mdi-file-document-edit', route:'/jenis-dokumen-organisasi', tooltip: 'Jenis Dokumen Organisasi' },
      { id: 5, title: 'Pengajuan Dokumen', icon: 'mdi-book-plus', route:'/submit', tooltip: 'Pengajuan Dokumen' },
      { id: 6, title: 'Arsip Dokumen', icon: 'mdi-book', route:'/dokumen', tooltip: 'Arsip Dokumen' },
      { id: 7, title: 'Pengguna', icon: 'mdi-account', route:'/pengguna', tooltip: 'Pengguna' },
      { id: 8, title: 'Log Aktivitas', icon: 'mdi-account', route:'/log-aktivitas', tooltip: 'Log Aktivitas Pengguna' },
      { id: 9, title: 'Template', icon: 'mdi-account', route:'/template', tooltip: 'Template' },
      { id: 10, title: 'Daftar Permohonan Tandatangan', icon: 'mdi-fountain-pen-tip', route:'/sign', tooltip: 'Daftar Permohonan Tandatangan' },
      { id: 11, title: 'Ganti Password', icon: 'mdi-account', route:'/ganti-password', tooltip: 'Ganti Password' },
    ],
    peranItems: [
      {kode_peran: "A01", nama_peran: "Administrator", menu: [1,2,3,4,5,6,7,8,11]},
      {kode_peran: "A02", nama_peran: "Pengaju", menu: [1,5,6,11]},
      {kode_peran: "A03", nama_peran: "Penandatangan", menu: [1,6,10,11]},
      {kode_peran: "A04", nama_peran: "Auditor", menu: [1,2,3,4,6,7,8,11]},
    ],
    right: null,
    user: [],
    currentUser: null,
    imageSrc: "",
    fileFoto: null,
    encodedFoto: "",
    organisasi: {
      nama: "",
    },
    institusi: {
      nama: "",
    },
    perans: [],
    peranselected: "",
    mini: true,
    hidden: false,
    hover: true,
    title: null,
    snackbar: false,
    snackbarText: "",
    snackbarColor: "",
    currentIp: "",
    rejectCount:0,
    confirmOrDenyRejectCount:0,
    batchFinishedCount:0,
    bulkFinishedCount:0,
    tokenType: "",
    logoutTimer: null,
    currentToken: "",
    menuItems: [],
    currentPeran: ""
  }),

  watch: {
    $route(val) {
      this.title = val.name;
      // close the mobile menu after navigating
      this.setDrawer(false);
    },
  },

  computed: {
    ...mapState("app", ["image", "color"]),
    inputValue: {
      get() {
        return this.$store.state.app.drawer;
      },
      set(val) {
        this.setDrawer(val);
      },
    },
    visibleMenus() {
      // Same menu list as before, de-duplicated by id (a menu can be mapped to a role twice)
      const seen = {};
      const menus = (this.menuItems || []).filter(m => {
        if (seen[m.id]) return false;
        seen[m.id] = true;
        return true;
      });
      // Reorder the signing menus among the slots they already occupy; other menus keep their place
      const order = ['/sign', '/batch-sign', '/verify-file'];
      const slots = [];
      menus.forEach((m, i) => { if (order.includes(m.route)) slots.push(i); });
      const sorted = slots.map(i => menus[i]).sort((a, b) => order.indexOf(a.route) - order.indexOf(b.route));
      slots.forEach((slot, i) => { menus[slot] = sorted[i]; });
      return menus;
    },
    navGroups() {
      return this.visibleMenus.filter(m => m.route == 'none' && this.childrenOf(m).length > 0);
    },
    navItems() {
      return this.visibleMenus.filter(m => m.route !== null && m.route !== '' && m.route !== 'none'
        && (m.id_parent == null || m.id_parent === '')
        && this.validForKeycloack(m.nama));
    },
    displayName() {
      const pengguna = (this.currentUser && this.currentUser.pengguna) || {};
      return pengguna.nama_lengkap || (this.currentUser && this.currentUser.username) || "";
    },
    roleName() {
      const peran = (this.perans || []).find(p => p.kode_peran === this.peranselected);
      return peran ? peran.nama : "";
    },
    userInitials() {
      const pengguna = (this.currentUser && this.currentUser.pengguna) || {};
      const name = pengguna.nama_lengkap || (this.currentUser && this.currentUser.username) || "";
      return name
        .split(/[\s._-]+/)
        .filter(Boolean)
        .slice(0, 2)
        .map(part => part.charAt(0).toUpperCase())
        .join("");
    },
    filteredItems(){
      console.log("peran item: " + this.peranselected);
      console.log(localStorage.getItem("currentPeran"));
      // Check account password status
      let status = localStorage.getItem("status");

      // if status is not ACTIVE, return only menu item with id 11
      // if ACTIVE, proceed
      if (status !== "ACTIVE") {
        return this.menuItems.filter(item => item.id === 11);
      }

      // find the peranItem with the matching kode_peran
      let listperanmenu = this.peranItems.find(item => item.kode_peran === this.peranselected);
      console.log(this.peranselected)
      // get the menu ids for the peranItem
      let menuIds = listperanmenu.menu;
      // filter the menuItems based on the menu ids
      let filteredMenu = this.menuItems.filter(item => menuIds.includes(item.id));
      return [];
    }
  },

  // mounted() {
  //   this.getPerans();
  // },
  created() {
    if (this.$store.state.snackbar.snackbarText != null) {
      this.showMessage();
    }
    
    let current = this.$router.currentRoute.path
    // if (current == '/ext/submit') {
    //   let token = this.$route.query.token
    //   apiServer.defaults.headers.common.Authorization = `Bearer ${token}`
    //   apiServer.defaults.headers.common['X-Token-Type'] = 'Internal'
    // }
    
    this.getPerans();
    this.currentUser = JSON.parse(localStorage.getItem("user"));
    this.tokenType = localStorage.getItem("token-type")
    this.setMenu()
    //alert('nn')
    //alert(JSON.stringify(this.menuItems))
    console.log("current user: " + JSON.stringify(this.currentUser.username))

    this.getNotifications()
    if (localStorage.getItem("currentPeran") == 'A03') {
      console.log("notif batch")
      // setInterval(this.getBatchFinishedList, 5000)
      this.getBulkFinishedList()
      this.getBatchFinishedList()
    }
    //this.peranselected = 'XX'
  },
  methods: {
    ...mapMutations("app", ["setDrawer", "toggleDrawer"]),

    toProfil() {
      //this.$forceUpdate();
      localStorage.setItem("currentPeran",this.peranselected);
      this.setMenu()

      this.getNotifications()

      if (this.peranselected == 'A03') {
          this.$router.push("/sign")
          window.location.reload();
      } else if (this.peranselected == 'A02') {
          this.$router.push("/submit")
      } else {
        this.$router.push("/profil")
      }
    },
    // async logoutSso() {
    //   await apiServer
    //       .post("/logout", {
    //         username: this.$store.state.user.username,
    //         waktuLogin: this.$store.state.user.waktuLogin
    //       })
    //       .then((response) => {
    //         console.log("Logout recorded")
    //       })
    //       .catch((error) => {
    //         console.log("There was an error:", error.response); // Logs out the error
    //       });
    //   await this.$store.dispatch("logout");
    //   window.location = 'http://10.15.90.117:8088'
    // },
    getPerans() {
      console.log("get perans")
      console.log(localStorage.getItem("currentPeran"))
      this.perans = JSON.parse(localStorage.getItem("user")).list_peran
      this.peranselected = localStorage.getItem("currentPeran");
      console.log("selected peran: " + this.peranselected)
      // for (var index in this.perans) {
      //   console.log("index role: ", index)
      //   this.perans[index].urutan = index ;
      // }
      // console.log("debug perans : ", this.perans);
      // if (this.$store.state.user.roleActive) {
      //   console.log("roleActive is on")
      //   this.peranselected = this.$store.state.user.roleActive.Id;
      // } else {
      //   this.peranselected = this.perans[0].Id;
      //   console.log("role selected ", this.role[this.peranselected])
      //   const user = localStorage.getItem("user");
      //   const userData = JSON.parse(user);
      //   const result = this.perans.find((obj) => {
      //     return obj.Id === this.peranselected;
      //   });
      //   console.log("result:")
      //   console.log(result)
      //   userData.roleActive = result;
      //   this.$store.commit("SET_USER_DATA", userData);
      //   console.log("debug user data : ", userData);
      // }
      // console.log("debug role selected : " + this.peranselected);
    },
    getNotifications() {
      if (localStorage.getItem("currentPeran") == 'A02') {
        apiServer
        .get("/arsip/reject_list", {
          params: {
            username_pengaju: this.currentUser.username
          }
        })
        .then(response => {
          var rejectItems = response.data.data;
          this.rejectCount = rejectItems.length
        })
        .catch(error => {
          console.log("There was an error:", error.response); // Logs out the error
          this.isLoading = false;
        });
      } else if (localStorage.getItem("currentPeran") == 'A03') {
        apiServer
        .get("/arsip/confirm_or_deny_reject_list", {
          params: {
            id_penandatangan_login: this.currentUser.id
          }
        })
        .then(response => {
          var confirmOrDenyReject = response.data.data;
          this.confirmOrDenyRejectCount = confirmOrDenyReject.length
        })
        .catch(error => {
          console.log("There was an error:", error.response); // Logs out the error
          this.isLoading = false;
        });
      }
    },
    getBulkFinishedList() { 
      apiServer
        .get("/arsip/finished_list_bulk", {
          params: {
            id_penandatangan_login: this.currentUser.id
          }
        })
        .then(response => { 
          var bulkFinished = response.data.data
          console.log("bulkFinished list:", bulkFinished.length)
          this.bulkFinishedCount = bulkFinished.length
        })
        .catch(error => {
          console.log("There was an error:", error); // Logs out the error 
        });
    },
    getBatchFinishedList() {
      console.log("getting finished list...")
      apiServer
        .get("/batch/finished_list", {
          params: {
            id_penandatangan_login: this.currentUser.id
          }
        })
        .then(response => { 
          var batchFinished = response.data.data
          console.log("finished list:", batchFinished.length)
          this.batchFinishedCount = batchFinished.length
          // if (finished.length > 0) {
          //   const batchNames = finished.map(function(i) {
          //       return i.batch_name
          //   })
          //   alert("Terdapat " + finished.length + " permohonan batch berhasil ditandatangan dengan nama batch: " + batchNames.join(", "))
          // }
        })
        .catch(error => {
          console.log("There was an error:", error); // Logs out the error 
        });
    },
    onChangeRole() {
      console.log("change role..");
      // this.resetRoleMenuSession(this.perans[i]);
      // apiServer
      //     .get("/checkIpUser/" + roleObj.idPeran)
      //     .then((response) => {
      //       this.resetRoleMenuSession(this.perans[i]);
      //     })
      //     .catch((error) => {
      //       console.log("there was an error:", error.response);
      //       if (error.response.data.message == "IP tidak terdaftar") {
      //         this.getPerans();
      //         this.showSnackbar(
      //             "error",
      //             "Alamat IP anda diluar daftar IP yang diizinkan"
      //         );
      //       }
      //     });
    },
    // resetRoleMenuSession(newRole) {
    //   const user = localStorage.getItem("user");
    //   const userData = JSON.parse(user);
    //   apiServer
    //     .get("/role-menu/role/" + newRole.idPeran)
    //     .then((response) => {
    //       userData.menus = response.data.data;
    //       userData.roleActive = newRole;
    //       console.log("new Role ", newRole);
    //       apiServer
    //         .get(
    //           "/user/change-role/" +
    //             newRole.idPeran +
    //             "/" +
    //             this.$store.state.user.username
    //         )
    //         .then((response) => {
    //           userData.token = response.data.data.token;
    //           userData.jenisTransaksis = response.data.data.jenisTransaksis;
    //           this.$store.commit("SET_USER_DATA", userData);
    //           console.log("new state ", this.$store.state.user);
    //           this.$router.replace("/home");
    //           location.reload();
    //         })
    //         .catch((error) => {
    //           console.log("There was an error:", error.response); // Logs out the error
    //         });
    //     })
    //     .catch((error) => {
    //       console.log("There was an error:", error.response); // Logs out the error
    //     });
    // },
    // getFotoUser() {
    //   apiServer
    //     .get("/userweb-profil/" + this.$store.state.user.userId)
    //     .then((response) => {
    //       this.user = response.data;
    //       if (this.user.fotoName !== null) {
    //         this.getFoto();
    //       } else {
    //         this.resetFoto();
    //       }
    //     })
    //     .catch((error) => {
    //       console.log("There was an error: ", error.response); // Logs out the error
    //       this.showSnackbar(
    //         "error",
    //         "Error, please contact your administrator!"
    //       );
    //     });
    // },
    // getFoto() {
    //   apiServer
    //     .get("/userweb-foto/" + this.$store.state.user.userId)
    //     .then((response) => {
    //       if (response.data != null) {
    //         this.encodedFoto = response.data;
    //         this.fileFoto = this.dataURLtoFile(
    //           this.encodedFoto,
    //           this.user.fotoName
    //         );
    //         const fileReader = new FileReader();
    //         fileReader.addEventListener("load", () => {
    //           this.imageSrc = fileReader.result;
    //         });
    //         fileReader.readAsDataURL(this.fileFoto);
    //       }
    //     });
    // },
    // resetFoto() {
    //   this.imageSrc = "";
    //   this.fileFoto = null;
    // },
    // dataURLtoFile(dataurl, filename) {
    //   var arr = dataurl.split(",");
    //   var mime = arr[0].match(/:(.*?);/)[1];
    //   var bstr = atob(arr[1]);
    //   var n = bstr.length;
    //   var u8arr = new Uint8Array(n);

    //   while (n--) {
    //     u8arr[n] = bstr.charCodeAt(n);
    //   }

    //   return new File([u8arr], filename, { type: mime });
    // },
    showSnackbar(color, message) {
      console.log("snack " + color + " - " + message);
      this.snackbar = true;
      this.snackbarText = message;
      this.snackbarColor = color;
    },
    showMessage() {
      this.showSnackbar(
        this.$store.state.snackbar.snackbarColor,
        this.$store.state.snackbar.snackbarText
      );
      this.$store.dispatch("setSnackbar", {
        snackbarColor: null,
        snackbarText: null,
      });
    },
    setMenu() {
      this.currentPeran = localStorage.getItem("currentPeran");
      this.menuItems =  this.currentUser.list_menu[this.currentPeran];

    },
    getIpAddress() {
      //real ip
      // fetch('https://api.ipify.org?format=json')
      //     .then(response => response.json())
      //     .then(response => {
      //       const user = localStorage.getItem("user");
      //       const userData = JSON.parse(user);
      //       userData.currentIp = response.data.data.ip
      //        this.currentIp = response.data.data.ip
      //       this.$store.commit("SET_USER_DATA", userData);
      //     });

      //ip mapping
      // apiServer
      //     .get("/ranmor/ip-mapping-user/" + this.$store.state.user.username)
      //     .then(response => {
      //       const user = localStorage.getItem("user");
      //       const userData = JSON.parse(user);
      //       userData.currentIp = response.data.data.ip
      //       this.currentIp = response.data.data.ip
      //       this.$store.commit("SET_USER_DATA", userData);
      //       console.log("debug user data : ", userData);
      //     })
      //     .catch(error => {
      //       console.log("There was an error:", error.response); // Logs out the error
      //     })
    },
    childrenOf(group) {
      return this.visibleMenus.filter(child => String(child.id_parent) === String(group.id) && this.validForKeycloack(child.nama));
    },
    isGroupActive(group) {
      const path = this.$route.path;
      return this.childrenOf(group).some(child => child.route && (path === child.route || path.startsWith(child.route + '/')));
    },
    badgeCount(nama) {
      if (nama === 'DaftarPermohonanTandatangan') return this.confirmOrDenyRejectCount + this.bulkFinishedCount;
      if (nama === 'DaftarPenolakan') return this.rejectCount;
      if (nama === 'DaftarPermohonanTandatanganBatch') return this.batchFinishedCount;
      return 0;
    },
    onBadgeClick(item) {
      // Kept from the sidebar: clicking the rejection badge clears its counter
      if (item.nama === 'DaftarPenolakan') this.rejectCount = 0;
    },
    titleCase(camelCase) {
      var result = camelCase.replaceAll("Daftar", '')
      result = result.replace(/([A-Z])/g, " $1");
      return result.charAt(0).toUpperCase() + result.slice(1);
    },
    validForKeycloack(camelCase) {
      if(this.tokenType === 'Keycloak') {
        return camelCase !== 'GantiPassword'
      } else {
        return true
      }
    }

  },
};
</script>

<!-- <style scoped>
.v-text-field--outlined:not(.v-input--is-focused):not(.v-input--has-state)
> .v-input__control
> .v-input__slot
fieldset {
color: rgba(221, 7, 7, 0.33)!important;}
</style> -->

<style scoped>
@import url("../../css/style.css");
@import url("../../css/drawer.css");
</style>

<style lang="scss">
/* Fix coming in v2.0.8 */
#core-app-bar {
  width: auto;
}

#core-app-bar a {
  text-decoration: none;
}

.tooltip {
  // font-family: Roboto !important;
  display: block !important;
  z-index: 10000;
}

.tooltip .tooltip-inner {
  background: rgb(88, 88, 88);
  color: white;
  border-radius: 0.25rem;
  padding: 5px 10px 4px;
}

.tooltip .tooltip-arrow {
  width: 0;
  height: 0;
  border-style: solid;
  position: absolute;
  margin: 5px;
  border-color: rgb(88, 88, 88);
  z-index: 1;
}

.tooltip[x-placement^="top"] {
  margin-bottom: 5px;
}

.tooltip[x-placement^="top"] .tooltip-arrow {
  border-width: 5px 5px 0 5px;
  border-left-color: transparent !important;
  border-right-color: transparent !important;
  border-bottom-color: transparent !important;
  bottom: -5px;
  left: calc(50% - 5px);
  margin-top: 0;
  margin-bottom: 0;
}

.tooltip[x-placement^="bottom"] {
  margin-top: 5px;
}

.tooltip[x-placement^="bottom"] .tooltip-arrow {
  border-width: 0 5px 5px 5px;
  border-left-color: transparent !important;
  border-right-color: transparent !important;
  border-top-color: transparent !important;
  top: -5px;
  left: calc(50% - 5px);
  margin-top: 0;
  margin-bottom: 0;
}

.tooltip[x-placement^="right"] {
  margin-left: 5px;
}

.tooltip[x-placement^="right"] .tooltip-arrow {
  border-width: 5px 5px 5px 0;
  border-left-color: transparent !important;
  border-top-color: transparent !important;
  border-bottom-color: transparent !important;
  left: -5px;
  top: calc(50% - 5px);
  margin-left: 0;
  margin-right: 0;
}

.tooltip[x-placement^="left"] {
  margin-right: 5px;
}

.tooltip[x-placement^="left"] .tooltip-arrow {
  border-width: 5px 0 5px 5px;
  border-top-color: transparent !important;
  border-right-color: transparent !important;
  border-bottom-color: transparent !important;
  right: -5px;
  top: calc(50% - 5px);
  margin-left: 0;
  margin-right: 0;
}

.tooltip.popover .popover-inner {
  background: #f9f9f9;
  color: black;
  padding: 24px;
  border-radius: 5px;
  box-shadow: 0 5px 30px rgba(black, 0.1);
}

.tooltip.popover .popover-arrow {
  border-color: #f9f9f9;
}

.tooltip[aria-hidden="true"] {
  visibility: hidden;
  opacity: 0;
  transition: opacity 0.15s, visibility 0.15s;
}

.tooltip[aria-hidden="false"] {
  visibility: visible;
  opacity: 1;
  transition: opacity 0.15s;
}

.v-list-item__title {
  white-space: pre-wrap!important;
}

// .v-icon__svg {
//   color: #ffffff;
// }
</style>